import React from 'react';
// components
import { FormattedMessage } from 'react-intl';
import FormattedWeight from 'components/FormattedWeight';
// api
import {
  removeExtendedInsightsResponsibleUser,
  removeTreatmentProtocolsResponsibleContact,
  setExtendedInsightsResponsibleUser,
  setTreatmentProtocolsResponsibleContact,
  removeReportsRestrictedUser,
  setReportsRestrictedUser,
  setVeterinaryWeeklySummaryUser,
  removeVeterinaryWeeklySummaryUser,
  setSalesInsightsWeeklyUser,
  removeSalesInsightsWeeklyUser,
  setCloseoutEmailUser,
  removeCloseoutEmailUser,
  setGroupCloseoutEmailUser,
  removeGroupCloseoutEmailUser,
  setManagerProductivityEmailUser,
  removeManagerProductivityEmailUser,
} from 'endpoints/tenantSettings';
// constants
import { hours, utcOffsets } from 'constants.js';

export const settingsLabels = {
  panelTitle: <FormattedMessage id="general.settings" />,
  updateSuccess: <FormattedMessage id="general.settings.updateSuccess" />,
  save: <FormattedMessage id="general.button.save" />,
  sms: {
    text: <FormattedMessage id="component.settings.smsComplianceMessaging" />,
    description: <FormattedMessage id="component.settings.smsComplianceDescription" />,
    firstReminder: <FormattedMessage id="component.settings.smsComplianceFirstReminder" />,
    secondReminder: <FormattedMessage id="component.settings.smsComplianceSecondReminder" />,
    remindHour: <FormattedMessage id="general.remindHour" />,
    timeZone: <FormattedMessage id="general.timeZone" />,
    reminderInfo: (firstHour, firstUtcOffset, secondHour, secondUtcOffset) => {
      const firstHourLabel = hours.find(({ value }) => value === firstHour)?.label || 'N/A';
      const firstOffsetLabel = utcOffsets.find(({ value }) => value === firstUtcOffset)?.label || 'N/A';
      const hasSecond = secondHour != null;
      const secondHourLabel = hasSecond ? hours.find(({ value }) => value === secondHour)?.label : null;
      const effectiveSecondOffset = secondUtcOffset ?? firstUtcOffset;
      const secondOffsetLabel = hasSecond ?
        (utcOffsets.find(({ value }) => value === effectiveSecondOffset)?.label || 'N/A') :
        null;
      return (
        <FormattedMessage
          id={hasSecond ?
            'component.settings.smsComplianceMessagingInfoTextTwo' :
            'component.settings.smsComplianceMessagingInfoText'}
          values={{
            hour: firstHourLabel,
            utcOffset: firstOffsetLabel,
            secondHour: secondHourLabel,
            secondUtcOffset: secondOffsetLabel,
            b: (msg) => <b>{msg}</b>
          }}
        />
      );
    }
  },
  inventory: {
    text: <FormattedMessage id="component.settings.invReconciliation" />,
    description: <FormattedMessage id="component.settings.invReconciliationDescription" />,
    reconciliationTimeFrame: <FormattedMessage id="component.settings.reconciliationTimeFrame" />,
    reconciliationInfo: (val) => (
      <FormattedMessage
        id="component.settings.invReconciliationInfoText"
        values={{
          weekDay: val,
          b: (msg) => <b>{msg}</b>
        }}
      />
    ),
  },
  extendedInsights: {
    text: <FormattedMessage id="general.extendedInsightsUsers" />,
    description: <FormattedMessage id="general.extendedInsightsUsers.desc" />,
  },
  reportsRestrictedUsers: {
    text: <FormattedMessage id="general.reportsRestrictedUsers" />,
    description: <FormattedMessage id="general.reportsRestrictedUsers.desc" />,
  },
  treatmentCompliance: {
    text: <FormattedMessage id="component.settings.treatmentCompliance" />,
    description: <FormattedMessage id="component.settings.treatmentComplianceDescription" />,
    days: <FormattedMessage id="component.settings.treatmentComplianceDays" />,
  },
  treatmentProtocols: {
    text: <FormattedMessage id="general.treatmentProtocols" />,
    description: <FormattedMessage id="component.settings.treatmentProtocolsDescription" />,
    responsibleUsers: {
      text: <FormattedMessage id="general.responsibleUsers" />,
      description: <FormattedMessage id="component.settings.treatmentProtocolsResponsibleUsers.desc" />,
    }
  },
  barnSheetsEditingConstraints: {
    text: <FormattedMessage id="component.settings.barnsheetsEditingConstraint" />,
    description: <FormattedMessage id="component.settings.barnsheetsEditingConstraint.desc" />,
  },
  emails: {
    text: <FormattedMessage id="general.pageTitle.autoReport" />,
    description: <FormattedMessage id="component.settings.autoReportDescription" />,
    autoReportTimeFrame: <FormattedMessage id="component.settings.autoReportTimeFrame" />
  },
  withdrawal: {
    text: <FormattedMessage id="component.settings.trackWithdrawalPeriods" />,
    description: <FormattedMessage id="component.settings.trackWithdrawalDescription" />,
    withdrawalWeightThreshold: <FormattedMessage id="component.settings.withdrawalWeightThreshold" />,
    info: (val) => (
      <FormattedMessage
        id="component.settings.trackWithdrawalInfoText"
        values={{ weight: <strong>{val}</strong>, measure: <FormattedWeight hasOnlyUnit /> }}
      />
    ),
  },
  customSurvey: {
    text: <FormattedMessage id="component.settings.customSurvey" />,
    description: <FormattedMessage id="component.settings.customSurveyDesc" />,
  },
  mortality: {
    text: <FormattedMessage id="component.settings.trackMortalityReasons" />,
    description: <FormattedMessage id="component.settings.mortalityReasonsDescription" />,
    manage: <FormattedMessage id="general.manageMortalityReasons" />,
  },
  comments: {
    text: <FormattedMessage id="component.settings.commentCompanyDirectPost" />,
    description: <FormattedMessage id="component.settings.commentCompanyDirectPostDescription" />,
  },
  autoReportMortalityPercentage: {
    text: <FormattedMessage id="component.settings.autoReportMortalityPercentage" />,
    description: <FormattedMessage id="component.settings.autoReportMortalityPercentage.desc" />,
  },
  mortalityCalculationsBaseline: {
    text: <FormattedMessage id="component.settings.mortalityCalculationsBaseline" />,
    description: <FormattedMessage id="component.settings.mortalityCalculationsBaseline.desc" />,
  },
  pigGroupsCompliance: {
    text: <FormattedMessage id="component.settings.treatmentProtocolsComplianceMessaging" />,
    description: <FormattedMessage id="component.settings.treatmentProtocolsComplianceMessaging.desc" />,
  },
  multiPlacementTreatmentProtocols: {
    text: <FormattedMessage id="component.settings.multiPlacementTreatmentProtocols" />,
    description: <FormattedMessage id="component.settings.multiPlacementTreatmentProtocols.desc" />,
  },
  complianceReport: {
    text: <FormattedMessage id="general.complianceReport" />,
    description: <FormattedMessage id="general.complianceReport.desc" />,
  },
  complianceFarmReport: {
    text: <FormattedMessage id="general.complianceFarmReport" />,
    description: <FormattedMessage id="general.complianceFarmReport.desc" />,
  },
  reportsDailySummary: {
    text: <FormattedMessage id="general.reportsDailySummary" />,
    description: <FormattedMessage id="general.reportsDailySummary.desc" />,
  },
  weeklyVeterinarySummary: {
    text: <FormattedMessage id="general.weeklyVeterinarySummary" />,
    description: <FormattedMessage id="general.weeklyVeterinarySummary.desc" />,
  },
  weeklySalesInsights: {
    text: <FormattedMessage id="general.weeklySalesInsights" />,
    description: <FormattedMessage id="general.weeklySalesInsights.desc" />,
    scheduleTitle: <FormattedMessage id="general.weeklySalesInsights.scheduleTitle" />,
    dayLabel: <FormattedMessage id="general.weeklySalesInsights.dayLabel" />,
    hourLabel: <FormattedMessage id="general.weeklySalesInsights.hourLabel" />,
    timezoneLabel: <FormattedMessage id="general.weeklySalesInsights.timezoneLabel" />,
  },
  caregiverMediaUpload: {
    text: <FormattedMessage id="general.caregiverMediaUpload" />,
    description: <FormattedMessage id="general.caregiverMediaUpload.desc" />,
  },
  waterConsumptionWarnings: {
    text: <FormattedMessage id="general.waterConsumptionWarnings" />,
    description: <FormattedMessage id="general.waterConsumptionWarnings.desc" />,
  },
  generalWithdrawalWarnings: {
    text: <FormattedMessage id="general.generalWithdrawalWarnings" />,
  },
  symptomPrompt: {
    text: <FormattedMessage id="component.settings.symptomPrompt" />,
    description: <FormattedMessage id="component.settings.symptomPromptDescription" />,
    days: <FormattedMessage id="component.settings.symptomPromptDays" />,
  },
  abfTracking: {
    text: <FormattedMessage id="component.settings.abfGroupTracking" />,
    description: <FormattedMessage id="component.settings.abfGroupTrackingDescription" />,
  },
  trackFeedOrders: {
    text: <FormattedMessage id="component.settings.trackFeedOrders" />,
    description: <FormattedMessage id="component.settings.trackFeedOrdersDescription" />,
  },
  feedDefaultDeliveryDate: {
    text: <FormattedMessage id="component.settings.feedDefaultDeliveryDate" />,
    description: <FormattedMessage id="component.settings.feedDefaultDeliveryDate.desc" />,
    cutoffLabel: <FormattedMessage id="component.settings.feedDefaultDeliveryDate.cutoffLabel" />,
    cutoffHint: <FormattedMessage id="component.settings.feedDefaultDeliveryDate.cutoffHint" />,
    daysLabel: <FormattedMessage id="component.settings.feedDefaultDeliveryDate.daysLabel" />,
    daysHint: <FormattedMessage id="component.settings.feedDefaultDeliveryDate.daysHint" />,
  },
  closeoutEmails: {
    text: <FormattedMessage id="general.closeoutEmailUsers" />,
    description: <FormattedMessage id="general.closeoutEmailUsers.desc" />,
  },
  groupCloseoutEmails: {
    text: <FormattedMessage id="general.groupCloseoutEmailUsers" />,
    description: <FormattedMessage id="general.groupCloseoutEmailUsers.desc" />,
  },
  managerProductivityEmails: {
    text: <FormattedMessage id="general.managerProductivityEmails" />,
    description: <FormattedMessage id="general.managerProductivityEmails.desc" />,
  },
  feedOrderFarmfeedEvent: {
    text: <FormattedMessage id="component.settings.feedOrderFarmfeedEvent" />,
    description: <FormattedMessage id="component.settings.feedOrderFarmfeedEvent.desc" />,
  },
};

export const getOptLabel = (options, value) => (options.find((opt) => opt.value === value) || {}).label;

export const filterOption = (selectedEntities) => () => (option) => {
  const isAlreadySelected = !!selectedEntities.find((entity) => entity.user.id === option.value);
  if (isAlreadySelected) return null;
  return option;
};

export const optionsMeasures = [
  { label: <FormattedMessage id="container.setPoints.system.usStandard" />, value: 'imperial' },
  { label: <FormattedMessage id="container.setPoints.system.metric" />, value: 'metric' },
];

export const optionsTemps = [
  { label: <FormattedMessage id="general.fahrenheit" />, value: 'fahrenheit' },
  { label: <FormattedMessage id="general.celsius" />, value: 'celsius' },
];

export const optionsSetpointSource = [
  {
    label: <FormattedMessage id="component.settings.temperatureSetpointSource.weightBased" />,
    value: 'weight_based_source'
  },
  {
    label: <FormattedMessage id="component.settings.temperatureSetpointSource.daysOnFeed" />,
    value: 'days_on_feed_source'
  },
];

export const responsibleUsersRequestMapper = {
  treatment_protocol_contacts: {
    set: setTreatmentProtocolsResponsibleContact,
    remove: removeTreatmentProtocolsResponsibleContact,
  },
  company_analytics_users: {
    set: setExtendedInsightsResponsibleUser,
    remove: removeExtendedInsightsResponsibleUser,
  },
  company_svrs_disabled_users: {
    set: setReportsRestrictedUser,
    remove: removeReportsRestrictedUser,
  },
  company_veterinary_weekly_summary_users: {
    set: setVeterinaryWeeklySummaryUser,
    remove: removeVeterinaryWeeklySummaryUser,
  },
  company_sales_insights_weekly_users: {
    set: setSalesInsightsWeeklyUser,
    remove: removeSalesInsightsWeeklyUser,
  },
  company_closeout_email_users: {
    set: setCloseoutEmailUser,
    remove: removeCloseoutEmailUser,
  },
  company_group_closeout_email_users: {
    set: setGroupCloseoutEmailUser,
    remove: removeGroupCloseoutEmailUser,
  },
  company_manager_productivity_email_users: {
    set: setManagerProductivityEmailUser,
    remove: removeManagerProductivityEmailUser,
  }
};
